import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";

interface LeadSubmission {
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  phone?: string;
  inquiry: string;
  organization?: string;
  agreedToTerms: boolean;
}

async function createLead(leadSubmission: LeadSubmission) {
  const response = await fetch("/leadSubmission/create", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(leadSubmission),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: "Failed to create lead" }));
    throw new Error(errorData?.message || "Failed to create lead");
  }

  const result = await response.json();
  console.log("Success! Created a lead");
  return result;
}

export function LeadForm() {
  const [charCount, setCharCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "", // for honeypot
    lastName: "",
    email: "",
    phone: "",
    inquiry: "",
    organization: "",
  });

  const isFormValid = useMemo(() => {
    return !!(
      formData.firstName.trim() &&
      formData.lastName.trim() &&
      formData.email.trim() &&
      formData.inquiry.trim()
    );
  }, [formData]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === "inquiry") {
      setCharCount(value.length);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Honeypot check
    if (formData.middleName.trim() !== "") {
      setIsSubmitting(false);
      return;
    }

    const leadData: LeadSubmission = {
      email: formData.email?.trim(),
      firstName: formData.firstName?.trim(),
      middleName: formData.middleName?.trim(),
      lastName: formData.lastName?.trim(),
      inquiry: formData.inquiry?.trim(),
      phone: formData.phone?.trim() || undefined,
      organization: formData.organization?.trim() || undefined,
      agreedToTerms: true,
    };

    try {
      await createLead(leadData);
      console.log("Lead submission successful:", leadData);

      setFormData({
        firstName: "",
        middleName: "",
        lastName: "",
        email: "",
        phone: "",
        inquiry: "",
        organization: "",
      });
      setCharCount(0);
      toast.success("Thank you for your interest! We'll be in touch soon.");
    } catch (err: unknown) {
      toast.error("Error submitting request. Please try again.");
      const handledError = err instanceof Error ? err : new Error("An error occurred.");
      console.error("Error creating lead:", handledError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ y: 25, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1.25, ease: "easeInOut" }}
      className="mx-auto max-w-xl"
    >
      <div className="rounded-2xl border border-border bg-card p-8 shadow-lg">
        <form onSubmit={handleSubmit}>
          {/* Honeypot field */}
          <div aria-hidden="true">
            <Input
              className="hidden"
              autoComplete="off"
              name="middleName"
              value={formData.middleName}
              onChange={handleChange}
              tabIndex={-1}
            />
          </div>

          <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
            <div>
              <label htmlFor="firstName" className="mb-2 block text-sm font-medium text-foreground">
                First name *
              </label>
              <Input
                id="firstName"
                name="firstName"
                type="text"
                autoComplete="given-name"
                value={formData.firstName}
                onChange={handleChange}
                required
                minLength={2}
                maxLength={50}
                className="border-border focus:border-ring focus:ring-ring"
              />
            </div>

            <div>
              <label htmlFor="lastName" className="mb-2 block text-sm font-medium text-foreground">
                Last name *
              </label>
              <Input
                id="lastName"
                name="lastName"
                type="text"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={handleChange}
                required
                minLength={2}
                maxLength={50}
                className="border-border focus:border-ring focus:ring-ring"
              />
            </div>

            <div className="sm:col-span-2">
              <div className="mb-2 flex justify-between">
                <label htmlFor="organization" className="block text-sm font-medium text-foreground">
                  Company or organization
                </label>
                <span className="text-sm text-muted-foreground">Optional</span>
              </div>
              <Input
                id="organization"
                name="organization"
                type="text"
                autoComplete="organization"
                value={formData.organization}
                onChange={handleChange}
                minLength={2}
                maxLength={50}
                className="border-border focus:border-ring focus:ring-ring"
              />
            </div>

            <div>
              <label htmlFor="email" className="mb-2 block text-sm font-medium text-foreground">
                Email *
              </label>
              <Input
                id="email"
                autoComplete="email"
                type="email"
                name="email"
                placeholder="you@your-company.com"
                value={formData.email}
                onChange={handleChange}
                maxLength={320}
                required
                pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,4}$"
                title="Please enter a valid email address"
                className="border-border focus:border-ring focus:ring-ring"
              />
            </div>

            <div>
              <div className="mb-2 flex justify-between">
                <label htmlFor="phone" className="block text-sm font-medium text-foreground">
                  Phone number
                </label>
                <span className="text-sm text-muted-foreground">Optional</span>
              </div>
              <Input
                id="phone"
                autoComplete="tel"
                type="tel"
                name="phone"
                placeholder="123-456-7890"
                value={formData.phone}
                onChange={handleChange}
                maxLength={20}
                pattern="^[+\d\s.\-\(\)]+$"
                title="Please enter a valid phone number"
                className="border-border focus:border-ring focus:ring-ring"
              />
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="inquiry" className="mb-2 block text-sm font-medium text-foreground">
                Tell us about your needs *
              </label>
              <Textarea
                id="inquiry"
                name="inquiry"
                placeholder="What type of verification do you need? How many users? What's your timeline?"
                value={formData.inquiry}
                onChange={handleChange}
                required
                minLength={10}
                maxLength={500}
                rows={5}
                className="resize-none border-border focus:border-ring focus:ring-ring"
              />
              <div className="mt-1 flex justify-end text-sm text-muted-foreground">
                {charCount}/500
              </div>
            </div>

            <div className="mt-8 sm:col-span-2">
              <Button
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className="w-full rounded-lg bg-primary px-6 py-3 font-medium text-primary-foreground transition-all duration-200 hover:scale-[1.02] hover:bg-primary/90 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
              >
                {isSubmitting ? "Submitting..." : "Request Demo"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
